/**
 * Import deduplication utility.
 *
 * Classifies each incoming item against the existing catalog. The same item is
 * defined by three things — its name, its vendor, and its size (unit) — but all
 * three are matched with a little tolerance for the drift that creeps in when a
 * name is re-keyed, OCR'd off a delivery photo, or written by hand:
 *
 *   Pass 0 — vendor SKU match
 *     The vendor's own product code under the same (or the same-supplier)
 *     vendor is exact identity, consulted before any name.
 *
 *   Pass 1 — strict key match
 *     normalize(name) + normalize(vendor) + normalize(unit), exact match, over
 *     the row's description AND its stored item_aliases spellings.
 *
 *   Pass 2 — fuzzy name match within the same vendor
 *     If pass 1 misses, look at existing items that share the same normalized
 *     vendor. First within the same (vendor, unit) bucket (name substring or
 *     token-set Jaccard >= 0.75); then a same-vendor bucket that tolerates unit
 *     formatting drift (name score >= 0.88) but still refuses to bridge two
 *     genuinely different sizes (a case of 750ml is not a case of 1L).
 *
 *   Pass 3 — vendor drift fallback
 *     If passes 1–2 miss, the same product at a compatible size can still match
 *     across a differently-written vendor name — but only on a strong name
 *     match, a compatible size, AND vendors that are the same supplier per
 *     vendorsSimilar: a matching account number (proof positive), or one name's
 *     distinctive words wholly contained in the other's ("Sysco" ⊆ "Sysco Los
 *     Angeles"). Conflicting names ("US Foods" vs "US Produce") need the account
 *     number. Vendor is never dropped: the same product from two different
 *     houses stays two items.
 *
 * Classification outcomes:
 *   'unchanged'    – matches and case_price is equal → skip, no DB write
 *   'price_change' – matches but case_price differs   → update existing
 *   'new'          – no match in any pass             → insert as new row
 *
 * Each result also carries a boolean `fuzzyMatched` flag so the UI can
 * optionally surface that the match was approximate rather than exact.
 */

function normalize(value) {
  return String(value ?? '')
    .toLowerCase()
    // Normalize smart quotes / dashes / non-breaking spaces to plain ASCII.
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/\u00A0/g, ' ')
    // Drop punctuation that varies harmlessly between sources (.,/_()*#&+).
    .replace(/[.,/\\_()*#&+'"`~!?:;]/g, ' ')
    .replace(/-/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function makeKey(name, vendor, unit) {
  return `${normalize(name)}|||${normalize(vendor)}|||${normalize(unit)}`;
}

// ── Vendor-product identity: the vendor's own SKU, then the stored aliases ───
// A vendor's product code is the vendor's OWN stable identity for a product,
// so it is consulted before any name. The same SKU on a later invoice is the
// same vendor product even when its description was re-keyed, its printed
// unit reads LB instead of CS, or the catalog row was renamed by the canonical
// naming pass (store 452, 2026-09-05: "AMERICAN CHEESE, YELLOW, 160 SLICE
// LOAF" no longer matched its renamed row "American Yellow Cheese" and landed
// as a second item). A SKU-less line keeps the name/vendor/unit passes below.
// The canonical rename pass files the vendor's original wording as an
// `item_aliases` row, so a catalog row may carry `aliases` — those spellings
// are matched exactly like its description. An AI display name is never the
// identity key on its own.
function skuKey(value) {
  return String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** The vendor product code carried on an item, however the caller spelled the field. */
function itemSku(item) {
  if (!item) return '';
  return skuKey(item.sku ?? item.product_code ?? item.supplier_sku ?? item.manufacturer_code ?? item.vendor_sku ?? '');
}

/** The stored alias spellings of a catalog row (strings, or {alias} rows). */
function itemAliases(item) {
  const list = Array.isArray(item?.aliases) ? item.aliases : [];
  return list
    .map((entry) => (typeof entry === 'string' ? entry : entry?.alias))
    .filter((alias) => normalize(alias));
}

function vendorSkuKey(vendor, sku) {
  const v = normalize(vendor);
  return v && sku ? `${v}|||${sku}` : null;
}

/**
 * The best name agreement between an incoming line and a catalog row, over the
 * row's description AND its stored aliases. Returns the score and how many
 * incoming tokens the winning spelling shares.
 */
function bestNameMatch(incomingName, incomingTokens, cand) {
  let best = { score: 0, sharedTokens: 0 };
  for (const name of [cand?.description, ...itemAliases(cand)]) {
    const score = fuzzyNameScore(incomingName, name);
    let sharedTokens = 0;
    tokenize(name).forEach((token) => { if (incomingTokens.has(token)) sharedTokens += 1; });
    if (score > best.score || (score === best.score && sharedTokens > best.sharedTokens)) {
      best = { score, sharedTokens };
    }
  }
  return best;
}

function bucketKey(vendor, unit) {
  const v = normalize(vendor);
  const u = normalize(unit);
  // Require BOTH vendor and unit to anchor a fuzzy bucket. Items missing
  // either field aren't safe to fuzzy-match against and are excluded from
  // the bucket index entirely.
  if (!v || !u) return null;
  return `${v}|||${u}`;
}

function vendorOnlyBucketKey(vendor) {
  const v = normalize(vendor);
  return v || null;
}

function tokenize(value) {
  const n = normalize(value);
  if (!n) return [];
  return n.split(' ').filter((t) => t.length > 0);
}

function jaccard(aTokens, bTokens) {
  if (aTokens.length === 0 || bTokens.length === 0) return 0;
  const a = new Set(aTokens);
  const b = new Set(bTokens);
  let inter = 0;
  a.forEach((t) => { if (b.has(t)) inter += 1; });
  const union = a.size + b.size - inter;
  return union === 0 ? 0 : inter / union;
}

const FUZZY_JACCARD_THRESHOLD = 0.75;
const FUZZY_MIN_SUBSTRING_LEN = 4;

function fuzzyNameScore(incomingName, candidateName) {
  const a = normalize(incomingName);
  const b = normalize(candidateName);
  if (!a || !b) return 0;
  if (a === b) return 1;

  const aTok = tokenize(a);
  const bTok = tokenize(b);
  const jac = jaccard(aTok, bTok);

  // Token-set Jaccard is the primary signal.
  let score = jac;

  // Substring containment is a secondary boost (helps with trailing
  // year/size suffixes like "Pinot Noir Signature" vs
  // "Pinot Noir Signature 2020"). To avoid false positives like
  // "Olive Oil 1L" matching "Olive Oil 4L Premium", we ONLY apply the
  // boost when there's also meaningful token overlap (at least two shared
  // tokens AND Jaccard >= 0.5). The boost still can't push acceptance on
  // its own — final result must clear FUZZY_JACCARD_THRESHOLD.
  const shorter = a.length <= b.length ? a : b;
  const longer  = a.length <= b.length ? b : a;
  const aSet = new Set(aTok);
  let sharedTokens = 0;
  bTok.forEach((t) => { if (aSet.has(t)) sharedTokens += 1; });

  if (
    shorter.length >= FUZZY_MIN_SUBSTRING_LEN
    && longer.includes(shorter)
    && sharedTokens >= 2
    && jac >= 0.5
  ) {
    score = Math.max(score, 0.85 + 0.15 * (shorter.length / longer.length));
  }

  return score;
}

// ── Vendor identity ───────────────────────────────────────────────────────
// The same supplier can be written several ways across invoices — re-keyed,
// OCR'd off a photo, or scrawled on a slip ("Sysco" one week, "Sysco Los
// Angeles" the next). Two vendors are the SAME supplier when either:
//
//   • They carry the same ACCOUNT NUMBER — the id the vendor assigns this store
//     — and share at least one distinctive word. That is proof positive, no
//     matter how differently the name is written ("Sysco" #A1 == "Sysco Los
//     Angeles" #A1).
//   • One name's distinctive words are wholly CONTAINED in the other's — one is
//     an extension of the same name ("Sysco" ⊆ "Sysco Los Angeles", "US" ⊆ "US
//     Produce"). Only the generic scaffolding is stripped first (Co/Inc/LLC,
//     "Distribution", "and"); industry words (Foods, Produce, Beverage, Spirits)
//     are KEPT distinctive, because "US Foods" and "US Produce" are different
//     houses. Two names that each carry a word the other lacks ("US Foods" vs
//     "US Produce", "Summit Beverage" vs "Summit Spirits") do NOT match on name
//     alone — only a matching account number can join them.
//
// Vendor stays a hard discriminator: a missing vendor, or names that share no
// distinctive word, never match.
const VENDOR_STOPWORDS = new Set([
  'co', 'inc', 'llc', 'llp', 'lp', 'ltd', 'corp', 'corporation', 'company',
  'companies', 'incorporated', 'plc', 'and', 'the', 'of',
  'distribution', 'distributions', 'distributing', 'distributor', 'distributors', 'dist',
]);

function vendorCoreTokens(vendor) {
  const tokens = tokenize(vendor);
  const core = tokens.filter((t) => !VENDOR_STOPWORDS.has(t));
  // All words were generic scaffolding ("Distribution Co") — keep the full list
  // so the name still compares against itself rather than becoming a wildcard.
  return core.length ? core : tokens;
}

/** Normalize an account number for comparison: drop spacing/punctuation, upcase. */
function accountKey(value) {
  return String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** The account number carried on an item, however the caller spelled the field. */
function itemAccount(item) {
  if (!item) return null;
  return item.accountNumber ?? item.account_number ?? item.account ?? null;
}

function vendorsSimilar(aVendor, bVendor, aAccount, bAccount) {
  const na = normalize(aVendor);
  const nb = normalize(bVendor);
  if (!na || !nb) return false;            // never bridge a missing vendor
  if (na === nb) return true;

  const coreA = vendorCoreTokens(aVendor);
  const coreB = vendorCoreTokens(bVendor);
  if (!coreA.length || !coreB.length) return false;

  const setA = new Set(coreA);
  const setB = new Set(coreB);
  let shared = 0;
  setA.forEach((t) => { if (setB.has(t)) shared += 1; });
  if (shared === 0) return false;          // must share a distinctive word

  // Proof positive: the store's account number with the vendor matches.
  const accA = accountKey(aAccount);
  const accB = accountKey(bAccount);
  if (accA && accB && accA === accB) return true;

  // Otherwise the smaller distinctive core must be wholly contained in the
  // larger (one name is an extension of the other). Conflicting distinctive
  // words fall through to "no match" and need an account number to join.
  return shared >= Math.min(setA.size, setB.size);
}

// ── Unit / size drift tolerance ───────────────────────────────────────────
// Units drift in formatting ("6/750ml" vs "6 / 750 ml") but a genuinely
// different size is a different product ("750ml" ≠ "1L"; a case of 6 ≠ a case of
// 12). unitsCompatible reduces each pack/size string to a comparable signature —
// a pack multiplier plus its measured contents, with volumes folded to
// millilitres so "1L" and "1000ml" agree — and only rejects when BOTH sides
// parse to a size AND those sizes clearly differ. Missing or unreadable units
// stay compatible so OCR noise never blocks an otherwise-solid match.
const UOM_TO_ML = {
  ml: 1, milliliter: 1, milliliters: 1, millilitre: 1, millilitres: 1,
  l: 1000, liter: 1000, liters: 1000, litre: 1000, litres: 1000,
};

function normalizeUnitText(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/ /g, ' ')  // non-breaking space → space
    .replace(/(\d)\s*[x×]\s*(?=\d)/g, '$1 ')   // "6x750" / "6 x 750" → "6 750"
    .replace(/(\d)(?=[a-z])/g, '$1 ')           // "6pk"/"750ml" → "6 pk"/"750 ml"
    .replace(/[/,()]/g, ' ')                    // pack separators
    .replace(/\s+/g, ' ')
    .trim();
}

function canonUom(u) {
  if (/^(oz|ozs|ounce|ounces|floz)$/.test(u)) return 'oz';
  if (/^(lb|lbs|pound|pounds)$/.test(u)) return 'lb';
  if (/^(gal|gallon|gallons)$/.test(u)) return 'gal';
  return u;
}

function parseUnitSignature(unit) {
  const n = normalizeUnitText(unit);
  if (!n) return null;
  const measureRe = /(\d+(?:\.\d+)?)\s*(milliliters?|millilitres?|ml|liters?|litres?|l|fl\s*oz|ounces?|ozs?|lbs?|pounds?|gallons?|gal|kg|g)\b/g;
  const contents = [];
  const stripped = n.replace(measureRe, (_full, num, uom) => {
    const val = parseFloat(num);
    const key = uom.replace(/\s+/g, '');
    contents.push(UOM_TO_ML[key] ? `${val * UOM_TO_ML[key]}ml` : `${val}${canonUom(key)}`);
    return ' ';
  });
  const bareNumbers = stripped.match(/\d+(?:\.\d+)?/g) || [];
  let pack = null;
  if (contents.length) {
    // Real measured contents exist → a leftover bare number is the pack
    // multiplier ("6" in "6 750ml").
    if (bareNumbers.length) pack = parseFloat(bareNumbers[0]);
  } else {
    // No unit-of-measure anywhere → a bare number IS the size signature, not a
    // pack. Without this, "7.5" and "1L" (and "6" vs "12") would both parse to
    // empty contents and slip through the lenient guard as "compatible".
    bareNumbers.forEach((x) => contents.push(String(parseFloat(x))));
  }
  if (!contents.length && pack == null) return null;
  contents.sort();
  return { pack, contents };
}

function unitsCompatible(a, b) {
  const na = normalizeUnitText(a);
  const nb = normalizeUnitText(b);
  if (na === nb) return true;
  if (!na || !nb) return true;                                   // unknown on one side → lenient
  const sa = parseUnitSignature(a);
  const sb = parseUnitSignature(b);
  if (!sa || !sb) return true;
  // Different pack multipliers are different sizes (a 6-pack is not a 24-pack),
  // even when neither side carries a measured content size. Check this first so
  // pack-only strings ("6 pk" vs "24 pk") don't slip through the lenient guard.
  if (sa.pack != null && sb.pack != null && sa.pack !== sb.pack) return false;
  if (!sa.contents.length || !sb.contents.length) return true;   // no measured size to compare
  return sa.contents.join('|') === sb.contents.join('|');
}

function toPrice(value) {
  if (value == null || value === '') return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function pricesEqual(a, b) {
  const na = toPrice(a);
  const nb = toPrice(b);
  if (na == null && nb == null) return true;
  if (na == null || nb == null) return false;
  return Math.abs(na - nb) < 0.001;
}

/**
 * Build lookup structures from the existing catalog.
 *
 *   exactByKey: Map<name|||vendor|||unit, item>
 *   byBucket:       Map<vendor|||unit, item[]> (strict fuzzy fallback)
 *   byVendorBucket: Map<vendor, item[]>        (OCR/unit-variance fallback)
 *   all:            item[]                      (vendor-drift fallback, scanned
 *                                                only when the buckets miss)
 *
 * @param {Array<object>} existingItems – rows from inventory_items
 */
export function buildCatalogLookup(existingItems) {
  const exactByKey = new Map();
  const byBucket = new Map();
  const byVendorBucket = new Map();
  const bySku = new Map();
  const bySkuOnly = new Map();
  const all = [];

  (existingItems || []).forEach((item) => {
    all.push(item);
    const key = makeKey(item.description, item.vendor, item.unit);
    if (key && !exactByKey.has(key)) {
      exactByKey.set(key, item);
    }
    // Pass 0 index: the vendor's product code. Vendor-scoped first; the
    // code-only list serves the vendor-drift check below.
    const sku = itemSku(item);
    if (sku) {
      const skuKeyed = vendorSkuKey(item.vendor, sku);
      if (skuKeyed && !bySku.has(skuKeyed)) bySku.set(skuKeyed, item);
      const skuList = bySkuOnly.get(sku);
      if (skuList) skuList.push(item);
      else bySkuOnly.set(sku, [item]);
    }
    // A stored alias is an exact spelling of this row: the vendor's original
    // wording before a rename, or a merged twin's name.
    itemAliases(item).forEach((alias) => {
      const aliasKey = makeKey(alias, item.vendor, item.unit);
      if (aliasKey && !exactByKey.has(aliasKey)) exactByKey.set(aliasKey, item);
    });
    const vendorBk = vendorOnlyBucketKey(item.vendor);
    if (vendorBk) {
      const vendorList = byVendorBucket.get(vendorBk);
      if (vendorList) vendorList.push(item);
      else byVendorBucket.set(vendorBk, [item]);
    }
    const bk = bucketKey(item.vendor, item.unit);
    if (bk) {
      const list = byBucket.get(bk);
      if (list) list.push(item);
      else byBucket.set(bk, [item]);
    }
    // If bk is null (vendor or unit missing) the item is intentionally
    // excluded from the fuzzy bucket index — exact-key match still works.
  });

  return { exactByKey, byBucket, byVendorBucket, bySku, bySkuOnly, all };
}

// Name-match floor for the vendor-drift fallback. Higher than the same-vendor
// fallback (0.88) because that pass ALSO relaxes the vendor, so the product name
// has to carry more of the confidence.
const FUZZY_DRIFT_NAME_THRESHOLD = 0.9;

function findFuzzyMatch(incomingItem, lookup) {
  const bk = bucketKey(incomingItem.vendor, incomingItem.unit);
  const vendorBk = vendorOnlyBucketKey(incomingItem.vendor);
  const incomingTokens = new Set(tokenize(incomingItem.description));

  const pickBest = (candidates, minScore, minSharedTokens, requireUnitCompatible) => {
    if (!candidates || candidates.length === 0) return null;
    let best = null;
    let bestScore = 0;
    let bestSharedTokens = 0;

    candidates.forEach((cand) => {
      // Same-vendor fallback tolerates unit *formatting* drift but must never
      // merge two genuinely different sizes (750ml vs 1L).
      if (requireUnitCompatible && !unitsCompatible(incomingItem.unit, cand.unit)) return;

      const { score, sharedTokens } = bestNameMatch(incomingItem.description, incomingTokens, cand);
      if (score < minScore) return;
      if (sharedTokens < minSharedTokens) return;

      if (score > bestScore || (score === bestScore && sharedTokens > bestSharedTokens)) {
        best = cand;
        bestScore = score;
        bestSharedTokens = sharedTokens;
      }
    });

    return best ? { item: best, score: bestScore } : null;
  };

  if (bk) {
    // Pass 2a — same (vendor, unit); unit already anchors the bucket.
    const strictBucketMatch = pickBest(lookup.byBucket.get(bk), FUZZY_JACCARD_THRESHOLD, 2, false);
    if (strictBucketMatch) return strictBucketMatch;
  }

  if (vendorBk) {
    // Pass 2b — same vendor, unit may vary in formatting but not in real size.
    const vendorMatch = pickBest(lookup.byVendorBucket.get(vendorBk), 0.88, 2, true);
    if (vendorMatch) return vendorMatch;
  }

  // Pass 3 — vendor drift. The exact-vendor buckets above can't see a re-keyed
  // vendor name, so scan the whole catalog for a strong name match at a
  // compatible size where the two vendor names share their distinctive core.
  const driftMatch = findVendorDriftMatch(incomingItem, lookup);
  if (driftMatch) return driftMatch;

  return null;
}

function findVendorDriftMatch(incomingItem, lookup) {
  const candidates = lookup.all || [];
  if (!candidates.length) return null;
  const incomingTokens = new Set(tokenize(incomingItem.description));
  const incomingVendorNorm = normalize(incomingItem.vendor);

  const qualifying = [];
  candidates.forEach((cand) => {
    const { score, sharedTokens } = bestNameMatch(incomingItem.description, incomingTokens, cand);
    if (score < FUZZY_DRIFT_NAME_THRESHOLD) return;
    // An exact name (score 1) may be a single distinctive word ("Hennessy");
    // anything short of exact must share at least two tokens to qualify.
    if (sharedTokens < (score >= 0.999 ? 1 : 2)) return;

    if (!vendorsSimilar(incomingItem.vendor, cand.vendor, itemAccount(incomingItem), itemAccount(cand))) return;
    if (!unitsCompatible(incomingItem.unit, cand.unit)) return;

    qualifying.push({
      cand,
      score,
      sharedTokens,
      vendorNorm: normalize(cand.vendor),
      vendorExact: normalize(cand.vendor) === incomingVendorNorm,
    });
  });

  if (!qualifying.length) return null;

  // An exact-vendor candidate always beats a drifted one — never let a
  // wrong-vendor row win just because its product name scored higher.
  const exact = qualifying.filter((q) => q.vendorExact);
  const pool = exact.length ? exact : qualifying;
  pool.sort((a, b) => (b.score - a.score) || (b.sharedTokens - a.sharedTokens));
  const top = pool[0];

  // When the match rests purely on vendor DRIFT (no exact-vendor candidate),
  // refuse to guess between two DIFFERENT drifted vendors that tie for the best
  // name score — that ambiguity means we can't tell which stored item the
  // invoice refers to, so leaving it "new" is the safe call.
  if (!exact.length) {
    const tiedVendors = new Set(
      pool.filter((q) => q.score === top.score).map((q) => q.vendorNorm),
    );
    if (tiedVendors.size > 1) return null;
  }

  return { item: top.cand, score: top.score };
}


/**
 * Pass 0 across a differently-written vendor name: the same product code under
 * a vendor that vendorsSimilar still resolves to the same supplier. Two
 * different houses can print the same code, so the vendor test is not relaxed
 * further, and two similar-vendor rows sharing the code is left alone.
 */
function findSkuVendorDriftMatch(incomingItem, sku, lookup) {
  const candidates = (lookup.bySkuOnly && lookup.bySkuOnly.get(sku)) || [];
  const qualifying = candidates.filter((cand) => (
    vendorsSimilar(incomingItem.vendor, cand.vendor, itemAccount(incomingItem), itemAccount(cand))
  ));
  if (!qualifying.length) return null;
  const vendors = new Set(qualifying.map((cand) => normalize(cand.vendor)));
  if (vendors.size > 1) return null;
  return qualifying[0];
}

/**
 * Classify a single incoming item against the catalog lookup.
 *
 * Returns:
 *   {
 *     classification: 'unchanged' | 'price_change' | 'new',
 *     existingItem: object|null,
 *     priceDeltaAbsolute: number|null,
 *     priceDeltaPct: number|null,
 *     fuzzyMatched: boolean,
 *   }
 */
export function classifyItem(incomingItem, catalogLookup) {
  // Back-compat: callers may still pass a plain Map (old shape).
  const lookup = catalogLookup && catalogLookup.exactByKey
    ? catalogLookup
    : { exactByKey: catalogLookup || new Map(), byBucket: new Map(), byVendorBucket: new Map(), all: [] };

  // Pass 0 — the vendor's product code under the same (or the same-supplier)
  // vendor. A SKU match is exact identity: it never asks the name or unit.
  const sku = itemSku(incomingItem);
  let existing = null;
  let fuzzyMatched = false;
  let skuMatched = false;
  if (sku) {
    const keyed = vendorSkuKey(incomingItem.vendor, sku);
    existing = (keyed && lookup.bySku && lookup.bySku.get(keyed)) || findSkuVendorDriftMatch(incomingItem, sku, lookup);
    skuMatched = Boolean(existing);
  }

  if (!existing) {
    const key = makeKey(incomingItem.description, incomingItem.vendor, incomingItem.unit);
    existing = lookup.exactByKey.get(key) || null;
  }

  if (!existing) {
    const fuzzy = findFuzzyMatch(incomingItem, lookup);
    if (fuzzy) {
      existing = fuzzy.item;
      fuzzyMatched = true;
    }
  }

  if (!existing) {
    return {
      classification: 'new',
      existingItem: null,
      priceDeltaAbsolute: null,
      priceDeltaPct: null,
      fuzzyMatched: false,
      skuMatched: false,
    };
  }

  const incomingPrice = toPrice(incomingItem.case_price);
  const existingPrice = toPrice(existing.case_price);

  if (pricesEqual(incomingPrice, existingPrice)) {
    return {
      classification: 'unchanged',
      existingItem: existing,
      priceDeltaAbsolute: null,
      priceDeltaPct: null,
      fuzzyMatched,
      skuMatched,
    };
  }

  const delta = (incomingPrice != null && existingPrice != null)
    ? incomingPrice - existingPrice
    : null;
  const pct = (delta != null && existingPrice != null && existingPrice !== 0)
    ? (delta / existingPrice) * 100
    : null;

  return {
    classification: 'price_change',
    existingItem: existing,
    priceDeltaAbsolute: delta,
    priceDeltaPct: pct,
    fuzzyMatched,
    skuMatched,
  };
}

/**
 * Classify all incoming items at once.
 *
 * Returns an array matching the input order, each element augmented with:
 *   _classification, _existingItem, _priceDeltaAbsolute, _priceDeltaPct,
 *   _fuzzyMatched.
 */
export function classifyImportItems(incomingItems, existingCatalog) {
  const lookup = buildCatalogLookup(existingCatalog);
  return (incomingItems || []).map((item) => {
    const result = classifyItem(item, lookup);
    return {
      ...item,
      _classification: result.classification,
      _existingItem: result.existingItem,
      _priceDeltaAbsolute: result.priceDeltaAbsolute,
      _priceDeltaPct: result.priceDeltaPct,
      _fuzzyMatched: result.fuzzyMatched,
    };
  });
}

/**
 * Apply a user-driven classification override to an item.
 *
 * Sets `_classificationOverride` so subsequent automatic re-classification
 * (e.g. at save time, or when the user edits the price) preserves the user's
 * intent. Also adjusts `_classification` and clears delta fields when the
 * override no longer represents a price change.
 */
export function overrideItemClassification(item, newClassification) {
  if (!item) return item;
  const next = {
    ...item,
    _classification: newClassification,
    _classificationOverride: newClassification,
  };
  if (newClassification !== 'price_change') {
    next._priceDeltaAbsolute = null;
    next._priceDeltaPct = null;
  }
  return next;
}

/**
 * Resolve an item's effective classification, honoring any user override.
 */
export function effectiveClassification(item) {
  if (!item) return null;
  return item._classificationOverride || item._classification || null;
}

/**
 * Deduplicate items WITHIN a single import batch.
 *
 * When a user imports multiple invoices at once, the same item may appear in
 * more than one invoice (sometimes at different prices). The desired rule is:
 * keep the first occurrence in import order and drop the rest, regardless of
 * whether the price matches — invoice dates aren't being read here, so we
 * can't pick a "newest" price reliably.
 *
 * Items are considered the same when their normalized
 * (description, vendor, unit) tuple is identical — the same strict key the
 * catalog dedup uses. Items missing description are passed through untouched.
 *
 * @param {Array<object>} items – items in import order (earliest first)
 * @returns {{ kept: Array<object>, dropped: Array<object> }}
 */
export function dedupeBatchItems(items) {
  const kept = [];
  const dropped = [];
  const seen = new Map();

  (items || []).forEach((item) => {
    const desc = normalize(item?.description);
    if (!desc) {
      kept.push(item);
      return;
    }
    // The same vendor product code on two lines of one batch (a second lot of
    // the same item, printed in the same unit) is one purchase; without a code
    // the (description, vendor, unit) tuple decides, as before.
    const sku = itemSku(item);
    const key = sku
      ? `sku|||${normalize(item.vendor)}|||${sku}|||${normalize(item.unit)}`
      : makeKey(item.description, item.vendor, item.unit);
    if (seen.has(key)) {
      dropped.push({ item, firstOccurrence: seen.get(key) });
    } else {
      seen.set(key, item);
      kept.push(item);
    }
  });

  return { kept, dropped };
}
